import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
// S-003 (2026-05-26): PostHog product analytics — defensive init.
// Silent no-op if REACT_APP_POSTHOG_KEY not set (dev/local default).
import { initAnalytics } from './utils/analytics';

// ─────────────────────────────────────────────────────────────────────────
// S-001A (2026-05-26): Sentry — production error tracking & performance monitoring.
// Webapp parity with mobile (which already has Sentry via crashReporting.js).
//
// DSN sources (priority):
//   1. REACT_APP_SENTRY_DSN env var (set in .env.production / Vercel / Netlify)
//   2. None → Sentry stays disabled (graceful no-op in dev/local)
//
// Production-grade settings:
//   • tracesSampleRate 0.1 — 10% of transactions traced (cost-efficient at scale)
//   • replaysOnErrorSampleRate 1.0 — 100% session replay on errors (critical for debugging)
//   • replaysSessionSampleRate 0.1 — 10% of regular sessions (cost-efficient)
//   • beforeSend PII stripping — strips email, IP, request bodies, tokens
//   • enabled: only in production (NODE_ENV check)
//   • ignoreErrors: network errors filtered (user-offline noise reduction)
//
// Rule G 4Q: All GREEN — additive observability layer, zero functional changes
// Rule Q (Parity): mirrors mobile crashReporting.js settings (Cat-1 parity)
// ─────────────────────────────────────────────────────────────────────────
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const IS_PROD = process.env.NODE_ENV === 'production';

if (SENTRY_DSN && IS_PROD && !SENTRY_DSN.includes('PLACEHOLDER')) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.NODE_ENV,
    release: 'papercrypto-webapp@1.7.0',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,         // Privacy: mask all text content
        blockAllMedia: true,       // Privacy: don't record images/videos
      }),
    ],
    // Performance monitoring (10% sample = cost-efficient at scale)
    tracesSampleRate: 0.1,
    // Session replay (10% normal, 100% on errors)
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // PII stripping — same logic as mobile crashReporting.js
    beforeSend(event) {
      if (event.user) {
        delete event.user.email;
        delete event.user.ip_address;
        // Keep id only (anonymized)
      }
      if (event.request?.data) delete event.request.data;
      if (event.extra?.password) delete event.extra.password;
      if (event.extra?.token) delete event.extra.token;
      return event;
    },
    // Filter user-offline noise (matches mobile crashReporting.js ignoreErrors)
    ignoreErrors: [
      'Network request failed',
      'AbortError',
      'ERR_NETWORK',
      'cancelled',
      'Failed to fetch',
      'Load failed',
    ],
  });
}

// S-003 (2026-05-26): Initialize PostHog product analytics.
// Silent no-op if REACT_APP_POSTHOG_KEY env var not set.
initAnalytics();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />); // StrictMode removed — caused double API calls in development
